import React, { useState, useEffect } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import * as FaIcons from "react-icons/fa";
import "../styles/Sidebar.css";
import { useSession } from "../firebase/UserProvider";
import { GetUserPrivilages } from "../firebase/UserPrivilageProvider";
import { useTaskRole } from "../hooks/useTaskRole";

// ─── Inline styles using CSS custom properties ──────────────────────────────
const S = {
  // Desktop sidebar panel
  sidebarPanel: {
    backgroundColor: "var(--theme-sidebar-bg)",
    borderRight: "1px solid var(--theme-color-mute)",
    height: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    paddingTop: "var(--theme-spacing-sm)",
  },
  // Mobile: full-height drawer overlay backdrop
  backdrop: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.3)",
    zIndex: "calc(var(--z-sidebar) - 1)",
  },
  // Mobile: sliding drawer
  drawer: {
    position: "fixed",
    top: 0,
    left: 0,
    bottom: 0,
    width: "260px",
    backgroundColor: "var(--theme-sidebar-bg)",
    zIndex: "var(--z-sidebar)",
    overflowY: "auto",
    paddingTop: "var(--theme-spacing-3xl)",
    boxShadow: "4px 0 16px rgba(0,0,0,0.15)",
    transition: "transform 0.25s ease",
  },
  navList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  sectionLabel: {
    fontFamily: "var(--theme-font-family-display)",
    fontSize: "11px",
    fontWeight: 500,
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    color: "var(--theme-color-body-mid)",
    padding: "var(--theme-spacing-lg) var(--theme-spacing-xl) var(--theme-spacing-xs)",
    display: "block",
  },
  navItem: {
    width: "100%",
    padding: "var(--theme-spacing-md) var(--theme-spacing-xl)",
    borderRadius: "var(--theme-rounded-sm)",
    cursor: "pointer",
    textDecoration: "none",
    fontFamily: "var(--theme-font-family-body)",
    fontSize: "14px",
    fontWeight: 400,
    color: "var(--theme-sidebar-text)",
    background: "none",
    border: "none",
    textAlign: "left",
    transition: "background 0.15s, color 0.15s",
    display: "flex",
    alignItems: "center",
    gap: "var(--theme-spacing-sm)",
  },
  navItemActive: {
    color: "var(--theme-sidebar-active-text)",
    backgroundColor: "var(--theme-sidebar-active-bg)",
    borderLeft: "3px solid var(--theme-sidebar-indicator)",
    paddingLeft: "calc(var(--theme-spacing-xl) - 3px)",
    fontWeight: 600,
  },
  subNavList: {
    listStyle: "none",
    padding: "0 0 0 var(--theme-spacing-xl)",
    margin: 0,
  },
  subNavItem: {
    display: "block",
    width: "100%",
    padding: "var(--theme-spacing-sm) var(--theme-spacing-lg)",
    borderRadius: "var(--theme-rounded-sm)",
    cursor: "pointer",
    textDecoration: "none",
    fontFamily: "var(--theme-font-family-body)",
    fontSize: "13px",
    color: "var(--theme-sidebar-text)",
    background: "none",
    border: "none",
    textAlign: "left",
    transition: "background 0.15s, color 0.15s",
  },
  chevron: {
    marginLeft: "auto",
    fontSize: "11px",
    transition: "transform 0.2s",
  },
};

function NavLink({ to, children, onClick, style }) {
  const location = useLocation();
  const isActive = location.pathname === to;
  return (
    <Link
      to={to}
      onClick={onClick}
      aria-current={isActive ? "page" : undefined}
      style={{
        ...S.navItem,
        ...(isActive ? S.navItemActive : {}),
        ...style,
      }}
    >
      {children}
    </Link>
  );
}

function Sidebar({ isOpen, onClose }) {
  const [donorNavMenuDropdown, setDonorNavMenuDropdown] = useState(false);
  const [tasksNavMenuDropdown, setTasksNavMenuDropdown] = useState(false);
  const [reportsNavMenuDropdown, setReportsNavMenuDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const history = useHistory();
  const { user } = useSession();
  const privilages = GetUserPrivilages();
  const { isTaskUser, isTaskAdmin } = useTaskRole();

  const toggleDonorNavMenuDropdown = () =>
    setDonorNavMenuDropdown((p) => !p);
  const toggleTasksNavMenuDropdown = () =>
    setTasksNavMenuDropdown((p) => !p);
  const toggleReportsNavMenuDropdown = () =>
    setReportsNavMenuDropdown((p) => !p);

  // Track viewport for responsive behaviour
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  const navigate = (path, closers = []) => {
    history.push(path);
    closers.forEach((fn) => fn());
    if (onClose) onClose();
  };

  if (!user) return null;

  const navContent = (
    <nav role="navigation" aria-label="Main navigation">
    <div style={S.sidebarPanel}>
      <ul style={S.navList}>
        {/* Home */}
        <li>
          <NavLink to="/home" onClick={onClose}>
            <FaIcons.FaHome />
            Home
          </NavLink>
        </li>

        {/* ── Donor Management System ── */}
        {privilages.canAccessDMS && (
          <>
            <li>
              <span style={S.sectionLabel}>Donor Management</span>
            </li>
            <li>
              <button
                style={S.navItem}
                onClick={toggleDonorNavMenuDropdown}
                aria-expanded={donorNavMenuDropdown}
              >
                <FaIcons.FaHandHoldingHeart />
                DMS
                <span
                  style={{
                    ...S.chevron,
                    transform: donorNavMenuDropdown ? "rotate(180deg)" : "rotate(0)",
                  }}
                >
                  ▾
                </span>
              </button>
              {donorNavMenuDropdown && (
                <ul style={S.subNavList}>
                  <li>
                    <button
                      style={S.subNavItem}
                      onClick={() =>
                        navigate("/donorsearchbyname", [toggleDonorNavMenuDropdown])
                      }
                    >
                      Search Donor by Name
                    </button>
                  </li>
                  <li>
                    <button
                      style={S.subNavItem}
                      onClick={() =>
                        navigate("/donorsearch", [toggleDonorNavMenuDropdown])
                      }
                    >
                      Search Donor by PAN
                    </button>
                  </li>
                  {privilages.canCreateDonor && (
                    <li>
                      <button
                        style={S.subNavItem}
                        onClick={() =>
                          navigate("/addnewdonor", [toggleDonorNavMenuDropdown])
                        }
                      >
                        Add a New Donor
                      </button>
                    </li>
                  )}
                  {privilages.canAccessReports && (
                    <li>
                      <button
                        style={S.subNavItem}
                        onClick={() =>
                          navigate("/donorreporthome", [toggleDonorNavMenuDropdown])
                        }
                      >
                        Donor Reports
                      </button>
                    </li>
                  )}
                </ul>
              )}
            </li>
          </>
        )}

        {/* ── Task Management System ── */}
        {(isTaskUser || isTaskAdmin) && (
          <>
            <li>
              <span style={S.sectionLabel}>Task Management</span>
            </li>
            <li>
              <button
                style={S.navItem}
                onClick={toggleTasksNavMenuDropdown}
                aria-expanded={tasksNavMenuDropdown}
              >
                <FaIcons.FaTasks />
                TMS
                <span
                  style={{
                    ...S.chevron,
                    transform: tasksNavMenuDropdown ? "rotate(180deg)" : "rotate(0)",
                  }}
                >
                  ▾
                </span>
              </button>
              {tasksNavMenuDropdown && (
                <ul style={S.subNavList}>
                  <li>
                    <button
                      style={S.subNavItem}
                      onClick={() =>
                        navigate("/tasks", [toggleTasksNavMenuDropdown])
                      }
                    >
                      Tasks List
                    </button>
                  </li>
                  {(isTaskUser || isTaskAdmin) && (
                    <>
                      <li>
                        <button
                          style={S.subNavItem}
                          onClick={toggleReportsNavMenuDropdown}
                        >
                          Reports
                          <span
                            style={{
                              ...S.chevron,
                              transform: reportsNavMenuDropdown ? "rotate(180deg)" : "rotate(0)",
                              marginLeft: "var(--theme-spacing-sm)",
                            }}
                          >
                            ▾
                          </span>
                        </button>
                        {reportsNavMenuDropdown && (
                          <ul style={{ ...S.subNavList, paddingLeft: "var(--theme-spacing-xl)" }}>
                            {isTaskAdmin && (
                              <li>
                                <button
                                  style={S.subNavItem}
                                  onClick={() =>
                                    navigate("/tasks/dashboard", [
                                      toggleReportsNavMenuDropdown,
                                      toggleTasksNavMenuDropdown,
                                    ])
                                  }
                                >
                                  Task Dashboard
                                </button>
                              </li>
                            )}
                            <li>
                              <button
                                style={S.subNavItem}
                                onClick={() =>
                                  navigate("/tasks/reports/by-user", [
                                    toggleReportsNavMenuDropdown,
                                    toggleTasksNavMenuDropdown,
                                  ])
                                }
                              >
                                Tasks by User
                              </button>
                            </li>
                            <li>
                              <button
                                style={S.subNavItem}
                                onClick={() =>
                                  navigate("/tasks/reports/by-state", [
                                    toggleReportsNavMenuDropdown,
                                    toggleTasksNavMenuDropdown,
                                  ])
                                }
                              >
                                Tasks by State
                              </button>
                            </li>
                            {isTaskAdmin && (
                              <li>
                                <button
                                  style={S.subNavItem}
                                  onClick={() =>
                                    navigate("/tasks/reports/labels", [
                                      toggleReportsNavMenuDropdown,
                                      toggleTasksNavMenuDropdown,
                                    ])
                                  }
                                >
                                  Labels Report
                                </button>
                              </li>
                            )}
                            <li>
                              <button
                                style={S.subNavItem}
                                onClick={() =>
                                  navigate("/tasks/reports/raised-by-me", [
                                    toggleReportsNavMenuDropdown,
                                    toggleTasksNavMenuDropdown,
                                  ])
                                }
                              >
                                Tasks Raised by Me
                              </button>
                            </li>
                            <li>
                              <button
                                style={S.subNavItem}
                                onClick={() =>
                                  navigate("/tasks/reports/pending-me", [
                                    toggleReportsNavMenuDropdown,
                                    toggleTasksNavMenuDropdown,
                                  ])
                                }
                              >
                                Tasks Pending Me
                              </button>
                            </li>
                            <li>
                              <button
                                style={S.subNavItem}
                                onClick={() =>
                                  navigate("/tasks/reports/team-workload", [
                                    toggleReportsNavMenuDropdown,
                                    toggleTasksNavMenuDropdown,
                                  ])
                                }
                              >
                                Team Workload
                              </button>
                            </li>
                          </ul>
                        )}
                      </li>
                    </>
                  )}
                  <li>
                    <button
                      style={S.subNavItem}
                      onClick={() =>
                        navigate("/tasks/labels/search", [toggleTasksNavMenuDropdown])
                      }
                    >
                      Labels
                    </button>
                  </li>
                </ul>
              )}
            </li>
          </>
        )}

        {/* ── Admin ── */}
        {privilages.isAdmin && (
          <>
            <li>
              <span style={S.sectionLabel}>Admin</span>
            </li>
            <li>
              <NavLink to="/searchuser" onClick={onClose}>
                <FaIcons.FaUsersCog />
                User Management{" "}
                {process.env.REACT_APP_IS_PROD === "false" ? "(Dev)" : ""}
              </NavLink>
            </li>
            {process.env.REACT_APP_IS_PROD === "false" && (
              <li>
                <NavLink
                  to="/donordetails/QZPi7jjNQ56JvnaTXqL7"
                  onClick={onClose}
                >
                  <FaIcons.FaVial />
                  Test Record
                </NavLink>
              </li>
            )}
          </>
        )}
      </ul>
    </div>
    </nav>
  );

  // Mobile: render as overlay drawer
  if (isMobile) {
    if (!isOpen) return null;
    return (
      <>
        <div style={S.backdrop} onClick={onClose} aria-hidden="true" />
        <aside style={S.drawer} aria-label="Navigation drawer">
          {navContent}
        </aside>
      </>
    );
  }

  // Desktop / tablet: render as static left panel
  return (
    <aside className="float-start sidebar" aria-label="Main navigation">
      {navContent}
    </aside>
  );
}

export default Sidebar;
